import { QUERY_PARAMS } from '@/constants';

const devices = ['ios', 'android', 'web'];
export type TDevice = (typeof devices)[number];

/**
 * Get query params from url.
 * @param paramName - string to get from query params.
 * @returns query params.
 */
export function getQueryParam(paramName: string): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(paramName) || 'web';
  } catch (error) {
    console.error(`Error getting query param "${paramName}":`, error);
    return null;
  }
}

/**
 * Check if the current device matches the given type.
 * @param deviceType - The type of device to check ('ios', 'android', 'kiosco').
 * @returns True if the device matches the given type.
 */
export function isDevice(deviceType: TDevice): boolean {
  try {
    const deviceQueryParam = getQueryParam(QUERY_PARAMS.DEVICE);
    return deviceQueryParam === deviceType;
  } catch (error) {
    console.error(`Error checking device type "${deviceType}":`, error);
    return false;
  }
}

/**
 * Checks if iOS analytics integration is ready to use.
 *
 * This function verifies the presence of the `webkit.messageHandlers.firebase.postMessage` API,
 * which is commonly used for communication between a web application and an iOS app's native layer.
 *
 * @returns {boolean} `true` if the iOS analytics integration is available; otherwise, `false`.
 */
function isIosAnalyticsReadyToUse(): boolean {
  return !!window?.webkit?.messageHandlers?.firebase?.postMessage;
}

/**
 * Checks if Android analytics integration is ready to use.
 *
 * This function verifies the presence of the `AnalyticsWebInterface.logEvent` API,
 * which is commonly used for communication between a web application and an Android app's native layer.
 *
 * @returns {boolean} `true` if the Android analytics integration is available; otherwise, `false`.
 */
function isAndroidAnalyticsReadyToUse(): boolean {
  return !!window?.AnalyticsWebInterface?.logEvent;
}

/**
 * Check if the current device is ready to use analytics.
 * @param deviceType - The type of device ('ios' or 'android').
 * @returns void
 */
export function isAnalyticsReadyToUse(deviceType: TDevice) {
  if (deviceType === 'ios' && !isIosAnalyticsReadyToUse()) {
    throw new Error('ios analytics is not ready');
  }
  if (deviceType === 'android' && !isAndroidAnalyticsReadyToUse()) {
    throw new Error('android analytics is not ready');
  }
  if (devices.includes(deviceType)) {
    return true;
  }
  throw new Error(`Device "${deviceType}" is not supported`);
}

export const isDeviceAndroid = () => isDevice('android');
export const isDeviceIos = () => isDevice('ios');
export const isDeviceKiosco = () => isDevice('kiosco');
